<div class="container-fluid complyon-page onboarding-handoff-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell onboarding-handoff-shell mx-auto">
    <div class="mb-3">
      <h1 class="complyon-title onboarding-handoff-title mb-2 text-l">Onboarding handoff</h1>
      <p class="complyon-subtitle onboarding-handoff-subtitle mb-0 text-s">
        The live link with OnboardOS. New entities originate and activate in OnboardOS, then hand over to Complyon for their compliance lifetime - and Complyon's rules flow back to govern intake.
      </p>
    </div>

    <div class="d-flex flex-column flex-xl-row align-items-start justify-content-between gap-3 mb-3 onboarding-handoff-toolbar">
      <div class="d-flex flex-wrap align-items-center gap-2 onboarding-handoff-tab-row" role="tablist" aria-label="Onboarding handoff tabs">
        <button
          v-for="tab in handoff_tabs"
          :key="tab.id"
          type="button"
          class="btn onboarding-handoff-tab-btn d-inline-flex align-items-center gap-2"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== null" class="onboarding-handoff-tab-count">{{ tab.count }}</span>
        </button>
      </div>

      <div class="d-flex align-items-center gap-3">
        <button type="button" class="btn btn-link text-decoration-none onboarding-handoff-reset-btn d-inline-flex align-items-center gap-2">
          <i class="bi bi-arrow-clockwise"></i>
          <span>Reset sample</span>
        </button>
        <button type="button" class="btn btn-outline-secondary onboarding-handoff-outline-btn d-inline-flex align-items-center gap-2">
          <i class="bi bi-box-arrow-up-right"></i>
          <span>Open OnboardOS</span>
        </button>
      </div>
    </div>

    <div v-if="active_tab === 'handovers'">
      <div class="row g-3 mb-3">
        <div
          v-for="metric in handover_metrics"
          :key="metric.label"
          class="col-12 col-md-6 col-xl-3"
        >
          <div class="complyon-card onboarding-handoff-metric-card rounded-4 p-3 h-100">
            <div class="onboarding-handoff-metric-label text-xs">{{ metric.label }}</div>
            <div class="onboarding-handoff-metric-value" :class="`is-${metric.accent}`">{{ metric.value }}</div>
            <div class="onboarding-handoff-metric-note text-xs">{{ metric.note }}</div>
          </div>
        </div>
      </div>

      <div class="onboarding-handoff-callout rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-diagram-3"></i>
        <p class="mb-0">
          <strong>2 of 6 fully discharged.</strong> Accepting a handover fans out into Suppliers/Employees, Evidence Vault, Risk register, Calendar, Training, Conflicts, Workflows, My Tasks and Reports - so no onboarding record dies after activation.
        </p>
      </div>

      <div class="complyon-panel onboarding-handoff-table-card rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="onboarding-handoff-section-title text-s">Newly onboarded - supplier, employee, partner, client</div>
            <div class="onboarding-handoff-section-subtitle text-xs">Who has just entered the compliance environment. Open one to review the full handover pack.</div>
          </div>
          <span class="badge rounded-pill onboarding-handoff-range-pill text-xs">Last 7 days</span>
        </div>

        <div class="table-responsive px-3 px-md-4 pb-3 pb-md-4 pt-3">
          <table class="table complyon-table align-middle mb-0 onboarding-handoff-table">
            <thead>
              <tr>
                <th class="ps-0 text-xs">Ref</th>
                <th class="text-xs">Entity</th>
                <th class="text-xs">Journey</th>
                <th class="text-xs">Landed in</th>
                <th class="text-xs">Onboarding risk</th>
                <th class="text-xs">Provisioned</th>
                <th class="pe-0 text-xs">Handoff status</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in handover_rows"
                :key="row.ref"
                :class="row.highlighted ? 'table-row' : ''"
                class="onboarding-handoff-table-row"
                role="button"
                tabindex="0"
                @click="open_drawer(row.drawer_key)"
                @keydown.enter.prevent="open_drawer(row.drawer_key)"
                @keydown.space.prevent="open_drawer(row.drawer_key)"
              >
                <td class="ps-0 fw-semibold onboarding-handoff-ref">{{ row.ref }}</td>
                <td class="fw-semibold">{{ row.entity }}</td>
                <td>{{ row.journey }}</td>
                <td>{{ row.landed_in }}</td>
                <td><span class="onboarding-handoff-risk">{{ row.onboarding_risk }}</span></td>
                <td><span class="onboarding-handoff-provisioned">{{ row.provisioned }}</span></td>
                <td class="pe-0">
                  <span class="badge rounded-pill complyon-badge onboarding-handoff-table-pill text-xs" :class="row.handoff_status_class">
                    {{ row.handoff_status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'documents_received'">
      <div class="onboarding-handoff-callout rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-folder2-open"></i>
        <p class="mb-0">
          <strong>Documents received from onboarding.</strong> Tax, CSD, BBBEE, banking, contracts, declarations, IDs and training records - collected once at intake, now the entity's permanent compliance record.
        </p>
      </div>

      <div class="complyon-panel onboarding-handoff-table-card rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="onboarding-handoff-section-title text-s">Document register</div>
            <div class="onboarding-handoff-section-subtitle text-xs">Every document received across all handovers, worst-status first.</div>
          </div>
          <span class="badge rounded-pill onboarding-handoff-range-pill text-xs">3 missing / expired</span>
        </div>

        <div class="table-responsive px-3 px-md-4 pb-3 pb-md-4 pt-3">
          <table class="table complyon-table align-middle mb-0 onboarding-handoff-table">
            <thead>
              <tr>
                <th class="ps-0 text-xs">Entity</th>
                <th class="text-xs">Journey</th>
                <th class="text-xs">Document</th>
                <th class="text-xs">Reference</th>
                <th class="text-xs">Expires</th>
                <th class="text-xs">Status</th>
                <th class="pe-0 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in document_rows"
                :key="row.drawer_key"
                :class="row.highlighted ? 'table-row' : ''"
                class="onboarding-handoff-table-row onboarding-handoff-document-row"
                role="button"
                tabindex="0"
                @click="open_drawer(row.drawer_key)"
                @keydown.enter.prevent="open_drawer(row.drawer_key)"
                @keydown.space.prevent="open_drawer(row.drawer_key)"
              >
                <td class="ps-0 fw-semibold">{{ row.entity }}</td>
                <td>{{ row.journey }}</td>
                <td>{{ row.document }}</td>
                <td>{{ row.reference }}</td>
                <td>{{ row.expires }}</td>
                <td class="pe-0">
                  <span class="badge rounded-pill complyon-badge onboarding-handoff-table-pill text-xs" :class="row.status_class">
                    {{ row.status }}
                  </span>
                </td>
                <td class="pe-0 text-end text-secondary onboarding-handoff-table-chevron">
                  <i class="bi bi-chevron-right"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'outstanding_obligations'">
      <div class="onboarding-handoff-callout rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-bell"></i>
        <p class="mb-0">
          <strong>Outstanding obligations.</strong> What must still be monitored after onboarding - accepted obligations become live items in Complyon's ongoing-compliance lifecycle.
        </p>
      </div>

      <div class="complyon-panel onboarding-handoff-table-card rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="onboarding-handoff-section-title text-s">Post-onboarding obligations</div>
            <div class="onboarding-handoff-section-subtitle text-xs">Everything carried over from intake that Complyon must now monitor.</div>
          </div>
          <span class="badge rounded-pill onboarding-handoff-range-pill text-xs">5 need action</span>
        </div>

        <div class="table-responsive px-3 px-md-4 pb-3 pb-md-4 pt-3">
          <table class="table complyon-table align-middle mb-0 onboarding-handoff-table">
            <thead>
              <tr>
                <th class="ps-0 text-xs">Entity</th>
                <th class="text-xs">Obligation</th>
                <th class="text-xs">Type</th>
                <th class="text-xs">Due</th>
                <th class="pe-0 text-xs">Status</th>
                <th class="pe-0 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in obligation_rows"
                :key="row.drawer_key"
                :class="row.highlighted ? 'table-row' : ''"
                class="onboarding-handoff-table-row onboarding-handoff-obligation-row"
                role="button"
                tabindex="0"
                :title="`Open ${row.obligation}`"
                @click="open_drawer(row.drawer_key)"
                @keydown.enter.prevent="open_drawer(row.drawer_key)"
                @keydown.space.prevent="open_drawer(row.drawer_key)"
              >
                <td class="ps-0 fw-semibold">{{ row.entity }}</td>
                <td>{{ row.obligation }}</td>
                <td>{{ row.type }}</td>
                <td>{{ row.due }}</td>
                <td class="pe-0">
                  <span class="badge rounded-pill complyon-badge onboarding-handoff-table-pill text-xs" :class="row.status_class">
                    {{ row.status }}
                  </span>
                </td>
                <td class="pe-0 text-end text-secondary onboarding-handoff-table-chevron">
                  <i class="bi bi-chevron-right"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'renewal_schedule'">
      <div class="onboarding-handoff-callout rounded-4 px-3 px-md-4 py-3 mb-3">
        <i class="bi bi-calendar2-event"></i>
        <p class="mb-0">
          <strong>Renewal schedule created.</strong> Expiry and review dates were generated automatically on activation - these feed the compliance calendar and trigger re-onboarding when due.
        </p>
      </div>

      <div class="complyon-panel onboarding-handoff-table-card rounded-4 overflow-hidden mb-3">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 px-md-4 pt-3 pt-md-4">
          <div>
            <div class="onboarding-handoff-section-title text-s">Auto-generated renewal schedule</div>
            <div class="onboarding-handoff-section-subtitle text-xs">Every expiry and its advance review date, earliest first.</div>
          </div>
          <button type="button" class="btn btn-outline-secondary onboarding-handoff-outline-btn d-inline-flex align-items-center gap-2">
            <i class="bi bi-calendar2-week"></i>
            <span>Open calendar</span>
          </button>
        </div>

        <div class="table-responsive px-3 px-md-4 pb-3 pb-md-4 pt-3">
          <table class="table complyon-table align-middle mb-0 onboarding-handoff-table">
            <thead>
              <tr>
                <th class="ps-0 text-xs">Entity</th>
                <th class="text-xs">Renewal item</th>
                <th class="text-xs">Review date</th>
                <th class="text-xs">Expiry date</th>
                <th class="pe-0 text-xs"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in renewal_rows"
                :key="row.drawer_key"
                :class="row.highlighted ? 'table-row' : ''"
                class="onboarding-handoff-table-row onboarding-handoff-renewal-row"
                role="button"
                tabindex="0"
                :title="`Open ${row.item}`"
                @click="open_drawer(row.drawer_key)"
                @keydown.enter.prevent="open_drawer(row.drawer_key)"
                @keydown.space.prevent="open_drawer(row.drawer_key)"
              >
                <td class="ps-0 fw-semibold">{{ row.entity }}</td>
                <td>{{ row.item }}</td>
                <td>{{ row.review_date }}</td>
                <td>{{ row.expiry_date }}</td>
                <td class="pe-0 text-end text-secondary onboarding-handoff-table-chevron">
                  <i class="bi bi-chevron-right"></i>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else-if="active_tab === 'bridge'">
      <div class="complyon-panel onboarding-handoff-connection-card rounded-4 overflow-hidden mb-3">
        <div class="d-flex flex-column flex-xl-row align-items-start align-items-xl-center justify-content-between gap-3 p-3 p-md-4">
          <div class="d-flex align-items-start gap-3 min-w-0">
            <span class="onboarding-handoff-connection-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
              <i class="bi bi-box-arrow-up-right"></i>
            </span>
            <div class="min-w-0">
              <div class="onboarding-handoff-connection-title">OnboardOS connection - healthy</div>
              <div class="onboarding-handoff-connection-meta">
                Last handshake 38 seconds ago - 412 items received this week - audit trail is bi-directional
              </div>
            </div>
          </div>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            <span class="badge rounded-pill onboarding-handoff-pill">
              <span class="onboarding-handoff-dot me-2"></span>
              Live
            </span>
            <button type="button" class="btn btn-outline-secondary onboarding-handoff-outline-btn d-inline-flex align-items-center gap-2">
              <i class="bi bi-box-arrow-up-right"></i>
              <span>Open OnboardOS</span>
            </button>
          </div>
        </div>
      </div>

      <div class="complyon-panel onboarding-handoff-section-card rounded-4 overflow-hidden mb-3">
        <div class="px-3 px-md-4 pt-3 pt-md-4">
          <div class="onboarding-handoff-section-title text-s">Where Complyon and OnboardOS meet</div>
          <div class="onboarding-handoff-section-subtitle text-xs">One entity, one record - OnboardOS originates it, activation hands it over, Complyon owns it for life</div>
        </div>

        <div class="border-top px-3 px-md-4 py-3 py-md-4">
          <div class="onboarding-handoff-bridge-grid">
            <div class="onboarding-handoff-bridge-card is-origin rounded-4 p-3 p-md-4 h-100">
              <div class="onboarding-handoff-bridge-eyebrow text-uppercase">OnboardOS</div>
              <div class="onboarding-handoff-bridge-title">Intake & gates</div>
              <div class="onboarding-handoff-bridge-copy">Applicant applies. Documents, verification, risk scoring, approvals and signing are collected one time.</div>
            </div>

            <div class="onboarding-handoff-bridge-connector">
              <span class="onboarding-handoff-bridge-connector-label">Activation</span>
              <i class="bi bi-arrow-right"></i>
            </div>

            <div class="onboarding-handoff-bridge-card is-origin rounded-4 p-3 p-md-4 h-100">
              <div class="onboarding-handoff-bridge-eyebrow text-uppercase">OnboardOS</div>
              <div class="onboarding-handoff-bridge-title">Activation event</div>
              <div class="onboarding-handoff-bridge-copy">The case goes live. Its full pack - evidence, controls, risk, approvals and decision - commits to Complyon and the case closes.</div>
            </div>

            <div class="onboarding-handoff-bridge-connector">
              <span class="onboarding-handoff-bridge-connector-label">Handover</span>
              <i class="bi bi-arrow-right"></i>
            </div>

            <div class="onboarding-handoff-bridge-card is-destination rounded-4 p-3 p-md-4 h-100">
              <div class="onboarding-handoff-bridge-eyebrow text-uppercase">Complyon</div>
              <div class="onboarding-handoff-bridge-title">Ongoing compliance</div>
              <div class="onboarding-handoff-bridge-copy">The entity lives here for its compliance lifetime - monitoring, refreshes, payments, audits and re-onboarding triggers.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3 mb-3 onboarding-handoff-bridge-lanes">
        <div class="col-12 col-xl-6">
          <div class="onboarding-handoff-lane-card rounded-4 overflow-hidden h-100">
            <div class="onboarding-handoff-lane-head px-3 px-md-4 pt-3 pt-md-4">
              <div class="d-flex align-items-start justify-content-between gap-2">
                <div class="min-w-0">
                  <div class="onboarding-handoff-lane-title">Received from OnboardOS on activation</div>
                  <div class="onboarding-handoff-lane-copy">Committed once; becomes the entity's permanent compliance record in Complyon.</div>
                </div>
                <span class="badge rounded-pill onboarding-handoff-lane-pill">Inbound</span>
              </div>
            </div>

            <div class="px-3 px-md-4 py-3 py-md-4">
              <div class="onboarding-handoff-list">
                <div
                  v-for="item in inbound_items"
                  :key="item.title"
                  class="onboarding-handoff-list-item d-flex align-items-center justify-content-between gap-3"
                >
                  <div class="d-flex align-items-start gap-3 min-w-0">
                    <span :class="['onboarding-handoff-list-icon d-inline-flex align-items-center justify-content-center flex-shrink-0', item.icon_class]">
                      <i class="bi" :class="item.icon"></i>
                    </span>
                    <div class="min-w-0">
                      <div class="onboarding-handoff-list-title">{{ item.title }}</div>
                      <div class="onboarding-handoff-list-copy">{{ item.copy }}</div>
                    </div>
                  </div>
                  <router-link :to="{ name: item.route_name }" class="onboarding-handoff-list-route text-decoration-none text-nowrap d-inline-flex align-items-center gap-1">
                    <span>{{ item.route_label }}</span>
                    <i class="bi bi-arrow-right-short"></i>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-6">
          <div class="onboarding-handoff-lane-card rounded-4 overflow-hidden h-100">
            <div class="onboarding-handoff-lane-head px-3 px-md-4 pt-3 pt-md-4">
              <div class="d-flex align-items-start justify-content-between gap-2">
                <div class="min-w-0">
                  <div class="onboarding-handoff-lane-title">Provided to OnboardOS</div>
                  <div class="onboarding-handoff-lane-copy">Complyon owns these rules; OnboardOS executes them at intake.</div>
                </div>
                <span class="badge rounded-pill onboarding-handoff-lane-pill">Outbound</span>
              </div>
            </div>

            <div class="px-3 px-md-4 py-3 py-md-4">
              <div class="onboarding-handoff-list">
                <div
                  v-for="item in outbound_items"
                  :key="item.title"
                  class="onboarding-handoff-list-item onboarding-handoff-list-item--clickable d-flex align-items-center justify-content-between gap-3"
                  role="button"
                  tabindex="0"
                  :title="`Open ${item.route_label}`"
                  @click="go_to_handoff_route(item.route_name)"
                  @keydown.enter.prevent="go_to_handoff_route(item.route_name)"
                  @keydown.space.prevent="go_to_handoff_route(item.route_name)"
                >
                  <div class="d-flex align-items-start gap-3 min-w-0">
                    <span :class="['onboarding-handoff-list-icon d-inline-flex align-items-center justify-content-center flex-shrink-0', item.icon_class]">
                      <i class="bi" :class="item.icon"></i>
                    </span>
                    <div class="min-w-0">
                      <div class="onboarding-handoff-list-title">{{ item.title }}</div>
                      <div class="onboarding-handoff-list-copy">{{ item.copy }}</div>
                    </div>
                  </div>
                  <span class="onboarding-handoff-list-route text-nowrap d-inline-flex align-items-center gap-1">
                    <span>{{ item.route_label }}</span>
                    <i class="bi bi-arrow-right-short"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="complyon-panel onboarding-handoff-fanout-panel rounded-4 overflow-hidden mb-3">
        <div class="px-3 px-md-4 pt-3 pt-md-4">
          <div class="onboarding-handoff-section-title text-s">Connects to - the activation fan-out</div>
          <div class="onboarding-handoff-section-subtitle text-xs">Accepting a handover provisions records into all of these modules - the guarantee that no onboarding record dies after activation.</div>
        </div>
        <div class="px-3 px-md-4 py-3 py-md-4">
          <div class="d-flex flex-wrap gap-2">
            <router-link v-for="mod in bridge_modules" :key="mod.label" :to="{ name: mod.route_name }" class="badge rounded-pill onboarding-handoff-bridge-chip d-inline-flex align-items-center gap-2 text-decoration-none">
              <i class="bi" :class="mod.icon"></i>
              <span>{{ mod.label }}</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>

    <div v-if="active_drawer" class="command-centre-drawer-backdrop" @click="close_drawer"></div>

    <aside v-if="active_drawer" class="command-centre-drawer onboarding-handoff-drawer" aria-label="Onboarding handoff drawer">
      <div class="command-centre-drawer-header border-bottom">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="command-centre-drawer-kicker text-uppercase">{{ active_drawer.section }}</div>
            <h2 class="command-centre-drawer-title mb-1 text-truncate">{{ active_drawer.title }}</h2>
            <div class="command-centre-drawer-subtitle text-truncate">{{ active_drawer.subtitle }}</div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <span class="badge rounded-pill" :class="active_drawer.status_class">{{ active_drawer.status }}</span>
            <button type="button" class="btn btn-link text-decoration-none p-0 command-centre-drawer-close" @click="close_drawer" aria-label="Close drawer">
              <i class="bi bi-x-lg fs-5"></i>
            </button>
          </div>
        </div>
      </div>

      <div ref="drawerBody" class="command-centre-drawer-body onboarding-handoff-drawer-body">
        <div class="onboarding-handoff-drawer-callout rounded-4 px-3 px-md-4 py-3 mb-3">
          <i class="bi bi-arrow-left-right"></i>
          <p class="mb-0">{{ active_drawer.intro }}</p>
        </div>

        <div class="onboarding-handoff-drawer-stats mb-3">
          <div v-for="stat in active_drawer.stats" :key="stat.label" class="onboarding-handoff-drawer-stat rounded-4 p-3">
            <div class="onboarding-handoff-drawer-stat-label text-uppercase">{{ stat.label }}</div>
            <div class="onboarding-handoff-drawer-stat-value" :class="`is-${stat.accent}`">{{ stat.value }}</div>
            <div class="onboarding-handoff-drawer-stat-note">{{ stat.note }}</div>
          </div>
        </div>

        <section class="mb-4">
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div>
              <div class="command-centre-drawer-section-title">{{ active_drawer.risk_label }}</div>
              <div class="command-centre-drawer-kicker text-uppercase text-success">{{ active_drawer.risk_status }}</div>
            </div>
            <span class="badge rounded-pill" :class="active_drawer.risk_status_class">{{ active_drawer.risk_status }}</span>
          </div>
          <div class="d-flex flex-wrap gap-2">
            <span v-for="tag in active_drawer.risk_tags" :key="tag" class="badge rounded-pill onboarding-handoff-drawer-chip">
              <i class="bi bi-shield-check"></i>
              <span>{{ tag }}</span>
            </span>
          </div>
        </section>

        <section class="mb-4">
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div>
              <div class="command-centre-drawer-section-title">{{ active_drawer.documents_label }}</div>
              <div class="command-centre-drawer-kicker text-uppercase text-success">{{ active_drawer.documents_status }}</div>
            </div>
          </div>
          <div class="d-flex flex-column gap-2">
            <article v-for="item in active_drawer.documents" :key="item.title" class="onboarding-handoff-drawer-item d-flex align-items-start justify-content-between gap-3">
              <div class="d-flex align-items-start gap-3 min-w-0">
                <span class="onboarding-handoff-drawer-item-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                  <i class="bi" :class="item.icon"></i>
                </span>
                <div class="min-w-0">
                  <div class="onboarding-handoff-drawer-item-title">{{ item.title }}</div>
                  <div class="onboarding-handoff-drawer-item-subtitle">{{ item.meta }}</div>
                </div>
              </div>
              <span class="badge rounded-pill text-nowrap fw-semibold" :class="item.status_class">{{ item.status }}</span>
            </article>
          </div>
        </section>

        <section class="mb-4">
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div>
              <div class="command-centre-drawer-section-title">{{ active_drawer.approvals_label }}</div>
              <div class="command-centre-drawer-kicker text-uppercase text-secondary">{{ active_drawer.approvals_status }}</div>
            </div>
          </div>
          <div class="onboarding-handoff-drawer-timeline">
            <article v-for="(item, index) in active_drawer.approvals" :key="item.title" :class="['onboarding-handoff-drawer-timeline-item', index === active_drawer.approvals.length - 1 ? 'is-last' : '']">
              <span class="onboarding-handoff-drawer-timeline-badge">
                <i class="bi" :class="item.icon"></i>
              </span>
              <span v-if="index !== active_drawer.approvals.length - 1" class="onboarding-handoff-drawer-timeline-line"></span>
              <div class="onboarding-handoff-drawer-timeline-content">
                <div class="onboarding-handoff-drawer-item-title">{{ item.title }}</div>
                <div class="onboarding-handoff-drawer-item-subtitle">{{ item.subtitle }}</div>
              </div>
            </article>
          </div>
        </section>

        <section class="mb-4">
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div>
              <div class="command-centre-drawer-section-title">{{ active_drawer.obligations_label }}</div>
              <div class="command-centre-drawer-kicker text-uppercase text-secondary">{{ active_drawer.obligations_status }}</div>
            </div>
          </div>
          <div class="d-flex flex-column gap-2">
            <article v-for="item in active_drawer.obligations" :key="item.title" class="onboarding-handoff-drawer-item d-flex align-items-start justify-content-between gap-3">
              <div class="d-flex align-items-start gap-3 min-w-0">
                <span class="onboarding-handoff-drawer-item-icon is-orange d-inline-flex align-items-center justify-content-center flex-shrink-0">
                  <i class="bi bi-clock-history"></i>
                </span>
                <div class="min-w-0">
                  <div class="onboarding-handoff-drawer-item-title">{{ item.title }}</div>
                  <div class="onboarding-handoff-drawer-item-subtitle">{{ item.subtitle }}</div>
                </div>
              </div>
              <span class="badge rounded-pill text-nowrap fw-semibold" :class="item.status_class">{{ item.status }}</span>
            </article>
          </div>
        </section>

        <section class="mb-2">
          <div class="d-flex align-items-start justify-content-between gap-2 mb-3">
            <div>
              <div class="command-centre-drawer-section-title">{{ active_drawer.downstream_label }}</div>
              <div class="command-centre-drawer-kicker text-uppercase text-secondary">{{ active_drawer.downstream_status }}</div>
            </div>
          </div>
          <div class="d-flex flex-column gap-2">
            <article v-for="item in active_drawer.downstream" :key="item.title" class="onboarding-handoff-drawer-item d-flex align-items-start justify-content-between gap-3">
              <div class="d-flex align-items-start gap-3 min-w-0">
                <span class="onboarding-handoff-drawer-item-icon is-teal d-inline-flex align-items-center justify-content-center flex-shrink-0">
                  <i class="bi bi-inboxes"></i>
                </span>
                <div class="min-w-0">
                  <div class="onboarding-handoff-drawer-item-title">{{ item.title }}</div>
                  <div class="onboarding-handoff-drawer-item-subtitle">{{ item.meta }}</div>
                </div>
              </div>
              <span class="badge rounded-pill text-nowrap fw-semibold" :class="item.status_class">{{ item.status }}</span>
            </article>
          </div>
        </section>
      </div>

      <div class="command-centre-drawer-footer border-top onboarding-handoff-drawer-footer">
        <div class="d-flex flex-wrap gap-2 align-items-center w-100">
          <button type="button" class="btn btn-link text-decoration-none text-secondary px-0" @click="close_drawer">
            <i class="bi bi-arrow-left me-1"></i>
            <span>{{ active_drawer.footer_secondary_label }}</span>
          </button>

          <div class="ms-auto">
            <button type="button" class="btn btn-primary-dark doa-route-btn d-inline-flex align-items-center gap-2" @click="go_to_handoff_route(active_drawer.footer_primary_route_name)">
              <i class="bi bi-box-arrow-up-right"></i>
              <span>{{ active_drawer.footer_primary_label }}</span>
            </button>
          </div>
        </div>
      </div>
    </aside>

    <div v-else class="onboarding-handoff-fallback rounded-4 p-3">
      Select a tab to view its handoff page.
    </div>
  </div>
</div>
